import { useId, useState } from "react";
import {
  styled,
  Text,
  Label as TGLabel,
  Switch as TGSwitch,
  XStack,
} from "tamagui";

import { stylex } from "../../utils";

const Label = styled(TGLabel, {
  name: "Label",
  variants: {
    variant: {},
  } as const,
});

const LabelText = styled(Text, {
  name: "LabelText",
  variants: {
    variant: {
      default: {
        color: "$gray-900",
      },
    },
    size: {
      md: {
        fontFamily: "$body",
        fontSize: "$3",
        fontWeight: "$5",
      },
    },
  } as const,
  defaultVariants: {
    size: "md",
    variant: "default",
  },
});

const SwitchBase = styled(TGSwitch, {
  name: "SwitchBase",
  padding: 0,
  outlineColor: "transparent",
  outlineWidth: 0,
  variants: {
    variant: {
      default: {
        backgroundColor: "$gray-200",
      },
    },
    size: {
      md: {
        width: "$4",
        height: "$2",
        borderWidth: 0,
      },
    },
    selected: {
      true: {
        backgroundColor: "$score-8",
        disabledStyle: {
          backgroundColor: "$gray-200",
        },
      },
      false: {
        disabledStyle: {
          backgroundColor: "$gray-100",
        },
      },
    },
  } as const,
  defaultVariants: {
    size: "md",
    variant: "default",
  },
});

const Thumb = styled(TGSwitch.Thumb, {
  name: "Thumb",
  variants: {
    variant: {
      default: {
        borderWidth: "$0.2",
        borderColor: "$gray-600",
        backgroundColor: "white",
      },
    },
    selected: {
      true: {
        borderColor: "$score-8",
        disabledStyle: {
          borderColor: "$gray-200",
        },
      },
      false: {
        disabledStyle: {
          borderColor: "$gray-100",
        },
      },
    },
    size: {
      md: {
        height: "$2",
        width: "$2",
      },
    },
  } as const,
  defaultVariants: {
    size: "md",
    variant: "default",
  },
});

export const SwitchWithLabel: React.FC<{
  label?: string;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  isChecked?: boolean;
}> = ({ label, onCheckedChange, disabled, isChecked = false }) => {
  const [checked, setChecked] = useState(isChecked);
  const id = useId();

  const switchId = "switch-id-" + id;

  return (
    <XStack data-id="switch-with-label" alignItems="center" gap={"$1"}>
      <Label data-id="switch-label" cursor="pointer" htmlFor={switchId}>
        <LabelText>{label}</LabelText>
      </Label>

      <SwitchBase
        data-id="switch"
        onCheckedChange={(checked) => {
          setChecked(checked);
          onCheckedChange?.(checked);
        }}
        cursor="pointer"
        checked={checked}
        selected={checked}
        id={switchId}
        disabled={disabled}
        group={true}
      >
        <Thumb
          data-id="switch-thumb"
          disabled={disabled}
          $group-hover={{
            ...stylex(
              [
                !checked,
                {
                  borderColor: "$gray-800",
                },
              ],
              [
                checked,
                {
                  borderColor: "$score-8",
                },
              ],
            ),
          }}
          selected={checked}
        />
      </SwitchBase>
    </XStack>
  );
};
